import React, { PropsWithChildren, useCallback, useRef, useState } from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Select as RadixSelect } from "radix-ui";
import { ChevronDown, ChevronUp, Check } from "lucide-react";

export type SelectProps<T> = PropsWithChildren<{
  options: T[];
  getOptionLabel: (opt: T) => React.ReactNode;
  getOptionDescription?: (opt: T) => React.ReactNode;
  getOptionValue: (opt: T) => string;
  getOptionDisabled?: (opt: T) => boolean;
  getOptionTracking?: (opt: T) => string;
  /** Icon rendered beside each dropdown item, but NOT projected into the trigger. */
  getOptionIcon?: (opt: T) => React.ReactNode;
  contentProps?: React.ComponentProps<typeof RadixSelect.Content>;
  triggerProps?: React.ComponentProps<typeof RadixSelect.Trigger>;
  rootProps?: React.ComponentProps<typeof RadixSelect.Root>;
  disabled?: boolean;
  triggerTracking?: string;
  error?: string;
  footer?: React.ReactNode;
  /** Renders a persistent label inside the trigger, e.g. "Status :" */
  insetLabel?: string;
}>;

interface SelectItemProps {
  children: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  value: string;
  disabled?: boolean;
  selectTracking?: string;
}

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  (
    { children, description, icon, disabled, selectTracking, ...props },
    forwardedRef,
  ) => {
    return (
      <RadixSelect.Item
        {...props}
        ref={forwardedRef}
        disabled={disabled}
        data-track={selectTracking}
        className={clsx(
          "flex items-center justify-between px-4 py-2 text-sm last-of-type:rounded-b-md hover:!border-none hover:outline-0 focus:outline-0",
          disabled
            ? "cursor-not-allowed text-gray-400"
            : "cursor-pointer hover:bg-blue-50 focus:bg-blue-50",
        )}
      >
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-2">
            {icon && (
              <span className="text-gray-500" aria-hidden>
                {icon}
              </span>
            )}
            <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
          </div>
          {description && (
            <span className="text-textSubtle mt-0.5 text-xs">
              {description}
            </span>
          )}
        </div>
        <RadixSelect.ItemIndicator>
          <Check className="text-blue-500" />
        </RadixSelect.ItemIndicator>
      </RadixSelect.Item>
    );
  },
);

export function Select<T>({
  contentProps,
  triggerProps,
  options,
  getOptionLabel,
  getOptionDescription,
  getOptionValue,
  getOptionDisabled,
  getOptionTracking,
  getOptionIcon,
  rootProps,
  disabled = false,
  triggerTracking,
  error,
  footer,
  insetLabel,
}: SelectProps<T>) {
  const { contentRef, onOpenStateChange, currentSide } = useSideState();
  const handleOpenStateChange = useCallback(
    (open: boolean) => {
      rootProps?.onOpenChange?.(open);
      onOpenStateChange(open);
    },
    [onOpenStateChange, rootProps],
  );

  const handleValueChange = useCallback(
    (value: string) => {
      const baseTrackingId = triggerTracking?.trim();
      const selectedOption = options.find(
        (opt) => getOptionValue(opt) === value,
      );
      const optionTrackingId = selectedOption
        ? getOptionTracking?.(selectedOption)?.trim()
        : undefined;
      const trackingValue =
        optionTrackingId ||
        (baseTrackingId ? `${baseTrackingId}-${value}` : undefined);

      if (trackingValue && typeof window !== "undefined" && window.freshpaint) {
        window.freshpaint.track("Select Option Clicked", {
          tracking_id: trackingValue,
          selected_value: value,
        });
      }
      rootProps?.onValueChange?.(value);
    },
    [triggerTracking, options, getOptionValue, getOptionTracking, rootProps],
  );

  return (
    <RadixSelect.Root
      {...rootProps}
      onValueChange={handleValueChange}
      onOpenChange={handleOpenStateChange}
      key={rootProps?.value}
      disabled={disabled}
    >
      <div className="space-y-1">
        <RadixSelect.Trigger
          {...triggerProps}
          aria-label="Options"
          data-track={triggerTracking}
          className={twMerge(
            clsx(
              "border-dividerBold text-textDefault bg-surface-default relative inline-flex w-64 cursor-pointer items-center justify-between gap-2 rounded-md border p-4 whitespace-nowrap transition outline-none focus:border-blue-500 data-[state=open]:border-blue-500",
              {
                "data-[state=open]:border-b-dividerBold rounded-b-md data-[state=open]:rounded-b-none":
                  currentSide === "bottom",
                "data-[state=open]:border-t-dividerBold rounded-t-md data-[state=open]:rounded-t-none":
                  currentSide === "top",
              },
              disabled && "cursor-not-allowed opacity-50",
              error && "text-textError border-red-500",
              triggerProps?.className,
            ),
          )}
        >
          {insetLabel && (
            <span className="text-textSubtle text-sm">{insetLabel}</span>
          )}
          <RadixSelect.Value
            placeholder={triggerProps?.placeholder || "Select an option"}
          />
          <RadixSelect.Icon className="text-textSubtle">
            <ChevronDown size={20} />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>

        {error && <div className="text-textError text-sm">{error}</div>}
      </div>

      <RadixSelect.Portal>
        <RadixSelect.Content
          {...contentProps}
          ref={contentRef}
          className={twMerge(
            clsx(
              "data-[state=closed]:animate-fadeOut data-[state=open]:animate-fadeIn text-textDefault bg-surface-default z-popover w-[var(--radix-select-trigger-width)] overflow-hidden border border-blue-500",
              {
                "rounded-b-md": currentSide === "bottom",
                "rounded-t-md": currentSide === "top",
              },
              contentProps?.className,
            ),
          )}
          position="popper"
        >
          <RadixSelect.ScrollUpButton>
            <ChevronUp size={20} />
          </RadixSelect.ScrollUpButton>
          <RadixSelect.Viewport>
            <RadixSelect.Group>
              {options.map((option: T) => (
                <SelectItem
                  key={getOptionValue(option)}
                  value={getOptionValue(option)}
                  description={getOptionDescription?.(option)}
                  icon={getOptionIcon?.(option)}
                  disabled={getOptionDisabled?.(option)}
                  selectTracking={
                    getOptionTracking?.(option) ??
                    (triggerTracking
                      ? `${triggerTracking}-${getOptionValue(option)}`
                      : undefined)
                  }
                >
                  {getOptionLabel(option)}
                </SelectItem>
              ))}
            </RadixSelect.Group>
            {footer && (
              <>
                <RadixSelect.Separator className="mx-2 my-1 h-px bg-gray-200" />
                <div className="px-4 py-2 text-xs text-gray-500">{footer}</div>
              </>
            )}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

type Side = RadixSelect.SelectContentProps["side"];

function useSideState() {
  const [currentSide, setCurrentSide] = useState<Side>("bottom");
  const contentRef = useRef<HTMLDivElement>(null);

  const onOpenStateChange = useCallback((open: boolean) => {
    if (open) {
      setTimeout(() => {
        const contentElement = contentRef.current;
        if (contentElement) {
          setCurrentSide((contentElement.dataset.side || "bottom") as Side);
        }
      }, 0);
    }
  }, []);

  return { onOpenStateChange, currentSide, contentRef };
}
